import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { NavLink, useParams } from "react-router";
import { useRelationshipsRelationshipShowQuery } from "@/services/backend/relationships/api/service";
import { Commitment } from "@/services/backend/relationships/types/relationship";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { CpyNameForDocument } from "@/services/backend/cpy/lib/cpy-name-for-document";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { formatDate, weekRFC3339 } from "@/shared/lib/utilities/date";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";

export function CommitmentRoute() {
  const { relationshipId, commitmentId } = useParams();

  const {
    data: relationship,
    error,
    isLoading,
  } = useRelationshipsRelationshipShowQuery({
    id: relationshipId!,
  });

  const commitment = relationship?.commitments.find(
    (c) => c.id === commitmentId,
  );

  const breadcrumbs = relationship && commitment && (
    <HtzBreadcrumbs
      customerId={relationship.customerId}
      processId={commitment.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/htz/${commitment.processId!}/commitments`}>
            {t("Sollstellungen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          {formatDate(commitment.date) +
            " (KW " +
            weekRFC3339(commitment.date) +
            ")"}
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {!isLoading && !error && !commitment && (
        <RTKQueryErrorAlert error={{ status: 404 }} />
      )}
      {commitment && <CommitmentPage commitment={commitment} />}
    </ProcessPageMainTemplate>
  );
}

function CommitmentPage({ commitment }: { commitment: Commitment }) {
  // The work order and positions tabs edit the work order of the
  // commitment; its field errors are kept in this scope.
  const links = [
    { to: "data", label: t("Sollstellung") },
    {
      to: "work-order",
      label: t("Arbeitsschein"),
      fieldName: [
        "executingBranchId",
        "beginDate",
        "endDate",
        "teamLeader",
        "internalComment",
        "address",
        "contact",
        "representative",
      ],
    },
    { to: "positions", label: t("Leistungen"), fieldName: ["positions"] },
  ];

  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <DocumentPageNavigation>
              {links.map((link) => (
                <DocumentPageNavigationLink
                  key={link.to}
                  to={link.to}
                  fieldName={link.fieldName}
                >
                  {link.label}
                </DocumentPageNavigationLink>
              ))}
            </DocumentPageNavigation>
            <CpyDialog
              destinationEntityName={CpyNameForDocument(
                GSMServiceKind.HTZ,
                commitment.documentKind!,
              )}
              destinationId={commitment.documentId!}
            />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <DocumentPageNavigationOutlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}
